import React from 'react';
import { Upload, Typography, Spin, Grid } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { supportedAudioFileTypes } from '../../utils/supportedAudioFileTypes';
import AudioFileAttached from './AudioFileAttached';
import toast from 'react-hot-toast';

const { Dragger } = Upload;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

export default function AudioUploadSection({ onFileAttached }) {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isUploadComplete, setIsUploadComplete] = React.useState(false);
  const [attachedFile, setAttachedFile] = React.useState(null);

  const handleUpload = async (file) => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('visit_audio_upload.errors.file_too_large'));
      return false;
    }

    setIsProcessing(true);
    try {
      // wait for the file to be uploaded
      await onFileAttached(file);
      setAttachedFile(file);
      setIsUploadComplete(true);
    } finally {
      setIsProcessing(false);
    }
    return false; // Prevent Upload from auto-uploading
  };

  const handleRemove = () => {
    setAttachedFile(null);
    setIsUploadComplete(false);
    onFileAttached(null);
  };

  const renderUploadContent = () => {
    if (isProcessing) {
      return (
        <div style={{ marginTop: 15 }}>
          <div className="ant-upload-drag-icon">
            <Spin size="large" />
          </div>
          <p className="ant-upload-text" style={{ marginTop: 10 }}>
            {t('common.uploading')}
          </p>
        </div>
      );
    }

    if (isUploadComplete && attachedFile) {
      return (
        <div className="ant-upload-drag-icon">
          <AudioFileAttached 
            fileName={attachedFile.name}
            onRemove={handleRemove}
          />
        </div>
      );
    }

    return (
      <>
        <p className="ant-upload-drag-icon">
          <CloudUploadOutlined style={{ fontSize: 40, color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">
          { screens.sm ? t('visit_audio_upload.upload_hint') : t('visit_audio_upload.upload_hint_mobile')}
        </p>
        <p className="ant-upload-hint">
          {t('visit_audio_upload.supported_formats', {
            formats: supportedAudioFileTypes.join(', ')
          })}
        </p>
      </>
    );
  };

  return (
    <div style={{ marginTop: 35, textAlign: 'left', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <CloudUploadOutlined style={{ marginRight: 8 }} />
        <Text>{t('visit_audio_upload.title_short')}</Text>
      </div>
      <Dragger
        accept={[...supportedAudioFileTypes, 'audio/*', 'video/mp4', 'video/3gpp'].join(',')}
        beforeUpload={handleUpload}
        showUploadList={false}
        multiple={false}
        disabled={isProcessing || isUploadComplete}
        style={{ 
          cursor: isUploadComplete ? 'default' : 'pointer'
        }}
        data-testid="audio-upload-dragger"
      >
        {renderUploadContent()}
      </Dragger>
    </div>
  );
} 